import * as Schema from "effect/Schema";
import {
  CommandId,
  IsoDateTime,
  MessageId,
  NonNegativeInt,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

export const VOICE_WS_METHODS = {
  transcribeClip: "voice.transcribeClip",
  synthesizeSpeech: "voice.synthesizeSpeech",
  conversationStart: "voice.conversation.start",
  conversationAppendAudio: "voice.conversation.appendAudio",
  conversationEndInput: "voice.conversation.endInput",
  conversationStop: "voice.conversation.stop",
  conversationEvents: "voice.conversation.events",
} as const;

const VoiceSessionId = TrimmedNonEmptyString;
export type VoiceSessionId = typeof VoiceSessionId.Type;

const VoiceProviderRequestId = TrimmedNonEmptyString;
export type VoiceProviderRequestId = typeof VoiceProviderRequestId.Type;

const VoiceAudioMimeType = TrimmedNonEmptyString.check(Schema.isMaxLength(120));
export type VoiceAudioMimeType = typeof VoiceAudioMimeType.Type;

const VoiceLanguage = TrimmedNonEmptyString.check(Schema.isMaxLength(32));
export type VoiceLanguage = typeof VoiceLanguage.Type;

export const VoiceAudioChunk = Schema.Struct({
  data: TrimmedNonEmptyString,
  mimeType: VoiceAudioMimeType,
  sequence: NonNegativeInt,
  sampleRate: Schema.optional(NonNegativeInt),
  durationMs: Schema.optional(NonNegativeInt),
});
export type VoiceAudioChunk = typeof VoiceAudioChunk.Type;

export const VoiceSettings = Schema.Struct({
  provider: Schema.optional(TrimmedNonEmptyString),
  sttModel: Schema.optional(TrimmedNonEmptyString),
  ttsModel: Schema.optional(TrimmedNonEmptyString),
  realtimeModel: Schema.optional(TrimmedNonEmptyString),
  voiceId: Schema.optional(TrimmedNonEmptyString),
  language: Schema.optional(VoiceLanguage),
});
export type VoiceSettings = typeof VoiceSettings.Type;

export const VoiceTranscribeClipInput = Schema.Struct({
  threadId: ThreadId,
  mimeType: VoiceAudioMimeType,
  data: TrimmedNonEmptyString,
  durationMs: Schema.optional(NonNegativeInt),
  settings: Schema.optional(VoiceSettings),
});
export type VoiceTranscribeClipInput = typeof VoiceTranscribeClipInput.Type;

export const VoiceTranscriptResult = Schema.Struct({
  text: Schema.String,
  language: Schema.optional(VoiceLanguage),
  durationMs: Schema.optional(NonNegativeInt),
  provider: TrimmedNonEmptyString,
  providerRequestId: VoiceProviderRequestId,
  createdAt: IsoDateTime,
});
export type VoiceTranscriptResult = typeof VoiceTranscriptResult.Type;

export const VoiceSynthesizeSpeechInput = Schema.Struct({
  threadId: ThreadId,
  text: TrimmedNonEmptyString,
  messageId: Schema.optional(MessageId),
  settings: Schema.optional(VoiceSettings),
});
export type VoiceSynthesizeSpeechInput = typeof VoiceSynthesizeSpeechInput.Type;

export const VoiceConversationStartInput = Schema.Struct({
  threadId: ThreadId,
  settings: Schema.optional(VoiceSettings),
});
export type VoiceConversationStartInput = typeof VoiceConversationStartInput.Type;

export const VoiceConversationStartResult = Schema.Struct({
  sessionId: VoiceSessionId,
  provider: TrimmedNonEmptyString,
  startedAt: IsoDateTime,
});
export type VoiceConversationStartResult = typeof VoiceConversationStartResult.Type;

export const VoiceConversationAppendAudioInput = Schema.Struct({
  sessionId: VoiceSessionId,
  sequence: NonNegativeInt,
  mimeType: VoiceAudioMimeType,
  data: TrimmedNonEmptyString,
  sampleRate: Schema.optional(NonNegativeInt),
});
export type VoiceConversationAppendAudioInput = typeof VoiceConversationAppendAudioInput.Type;

export const VoiceConversationAppendAudioResult = Schema.Struct({
  accepted: Schema.Boolean,
  bufferedChunks: NonNegativeInt,
});
export type VoiceConversationAppendAudioResult = typeof VoiceConversationAppendAudioResult.Type;

export const VoiceConversationEndInputInput = Schema.Struct({
  sessionId: VoiceSessionId,
  commandId: Schema.optional(CommandId),
  messageId: Schema.optional(MessageId),
});
export type VoiceConversationEndInputInput = typeof VoiceConversationEndInputInput.Type;

export const VoiceConversationEndInputResult = Schema.Struct({
  threadId: ThreadId,
  transcript: VoiceTranscriptResult,
});
export type VoiceConversationEndInputResult = typeof VoiceConversationEndInputResult.Type;

export const VoiceConversationStopInput = Schema.Struct({
  sessionId: VoiceSessionId,
});
export type VoiceConversationStopInput = typeof VoiceConversationStopInput.Type;

export const VoiceConversationStopResult = Schema.Struct({
  stopped: Schema.Boolean,
});
export type VoiceConversationStopResult = typeof VoiceConversationStopResult.Type;

export const VoiceConversationEventsInput = Schema.Struct({
  sessionId: VoiceSessionId,
});
export type VoiceConversationEventsInput = typeof VoiceConversationEventsInput.Type;

export const VoiceConversationEvent = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("listening"),
    sessionId: VoiceSessionId,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("user_transcript_delta"),
    sessionId: VoiceSessionId,
    delta: Schema.String,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("user_transcript_final"),
    sessionId: VoiceSessionId,
    transcript: VoiceTranscriptResult,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("assistant_text_delta"),
    sessionId: VoiceSessionId,
    delta: Schema.String,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("assistant_audio_delta"),
    sessionId: VoiceSessionId,
    audio: VoiceAudioChunk,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("assistant_audio_done"),
    sessionId: VoiceSessionId,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("turn_started"),
    sessionId: VoiceSessionId,
    threadId: ThreadId,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("turn_completed"),
    sessionId: VoiceSessionId,
    threadId: ThreadId,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("tool_started"),
    sessionId: VoiceSessionId,
    callId: TrimmedNonEmptyString,
    label: TrimmedNonEmptyString,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("tool_completed"),
    sessionId: VoiceSessionId,
    callId: TrimmedNonEmptyString,
    label: TrimmedNonEmptyString,
    status: Schema.Literals(["succeeded", "failed"]),
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("interrupted"),
    sessionId: VoiceSessionId,
    reason: Schema.optional(TrimmedNonEmptyString),
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("error"),
    sessionId: VoiceSessionId,
    message: TrimmedNonEmptyString,
    createdAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("closed"),
    sessionId: VoiceSessionId,
    reason: Schema.optional(TrimmedNonEmptyString),
    createdAt: IsoDateTime,
  }),
]);
export type VoiceConversationEvent = typeof VoiceConversationEvent.Type;

export class VoiceServiceError extends Schema.TaggedErrorClass<VoiceServiceError>()(
  "VoiceServiceError",
  {
    message: TrimmedNonEmptyString,
  },
) {}
